import { DeleteOutlineRounded, FilterListRounded, KeyboardArrowDownRounded } from "@mui/icons-material";
import { Badge, Button, Divider, IconButton, Menu, MenuItem, Select, Stack, TextField } from "@mui/material";
import type { RowData, Table } from "@tanstack/react-table";
import _ from "lodash";
import { useEffect, useState, type RefObject } from "react";
import { Controller, useFieldArray, useForm, useWatch } from "react-hook-form";
import { TableColumnFilterOperator, type TableColumnFilter } from "./utilities";

const OPERATORS_WITHOUT_VALUE: TableColumnFilterOperator[] = [
  TableColumnFilterOperator.IsEmpty,
  TableColumnFilterOperator.IsNotEmpty,
];

const DEFAULT_FORM_FILTERS: TableColumnFilter[] = [
  { columnId: "", operator: TableColumnFilterOperator.Contains, text: "" },
];

interface TableFiltersButtonFormState {
  filters: TableColumnFilter[];
}

interface Props<T> {
  table: RefObject<Table<T> | null>;
  columnFilters: TableColumnFilter[];
  onColumnFiltersChanged: (columnFilters: TableColumnFilter[]) => void;
}

function filterInvalidFilters(filters: TableColumnFilter[]): TableColumnFilter[] {
  // A valid filter has a filled in column and text string (except for operators that don't require a text string)
  return filters.filter(
    (filter) => filter.columnId !== "" && (filter.text !== "" || OPERATORS_WITHOUT_VALUE.includes(filter.operator)),
  );
}

const TableFiltersButton = <T extends RowData>(props: Props<T>) => {
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const { control, subscribe, reset } = useForm<TableFiltersButtonFormState>({
    defaultValues: { filters: props.columnFilters.length > 0 ? props.columnFilters : DEFAULT_FORM_FILTERS },
  });
  const { fields, append, remove, replace } = useFieldArray({ name: "filters", control: control });
  const filters = useWatch({ control, name: "filters" });
  const filterCount = filterInvalidFilters(props.columnFilters).length;

  useEffect(() => {
    // Skip replacing the field array when props already match what this form last committed, so the
    // round-trip through the parent doesn't regenerate field ids (and remount inputs) mid-keystroke.
    if (!_.isEqual(filters, props.columnFilters)) {
      replace(props.columnFilters.length > 0 ? props.columnFilters : DEFAULT_FORM_FILTERS);
    }
  }, [filters, props.columnFilters, replace]);

  useEffect(() => {
    const unsubscribe = subscribe({
      name: "filters",
      formState: {
        values: true,
      },
      callback: ({ values }) => {
        // Prevents a sync-loop where props changing would trigger another onColumnFiltersChanged call
        if (!_.isEqual(values.filters, props.columnFilters)) {
          props.onColumnFiltersChanged(values.filters);
        }
      },
    });

    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscribe, props.columnFilters, props.onColumnFiltersChanged]);

  const columns = props.table?.current
    ?.getAllLeafColumns()
    .filter((column) => column.getIsVisible() && column.accessorFn !== undefined);

  function handleAddFilterClicked() {
    append({ columnId: "", operator: TableColumnFilterOperator.Contains, text: "" });
  }

  function handleDeleteClicked(index: number) {
    if (filters.length === 1) {
      reset();
    } else {
      remove(index);
    }
  }

  return (
    <>
      <Badge color="primary" variant="dot" invisible={filterCount === 0}>
        <Button
          variant="tertiary"
          startIcon={<FilterListRounded />}
          endIcon={<KeyboardArrowDownRounded />}
          onClick={(e) => setMenuAnchor(e.currentTarget)}
        >
          {filterCount > 0 ? `${filterCount} ` : ""}Advanced Filters
        </Button>
      </Badge>

      <Menu
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        anchorEl={menuAnchor}
        slotProps={{
          paper: {
            sx: { minWidth: 225 },
          },
        }}
      >
        <Stack spacing={0.5} divider={<Divider variant="dashed" />}>
          <Stack spacing={0.5}>
            {fields.map((field, index) => (
              <Stack direction="row" spacing={0.5} key={field.id} sx={{ alignItems: "center" }}>
                <Controller
                  name={`filters.${index}.columnId`}
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      displayEmpty
                      slotProps={{
                        input: {
                          sx: {
                            width: 200,
                          },
                        },
                      }}
                    >
                      <MenuItem value="">Select a column...</MenuItem>
                      {columns?.map((column) => (
                        <MenuItem key={column.id} value={column.id}>
                          {typeof column.columnDef.header === "string" ? column.columnDef.header : column.id}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />

                <Controller
                  name={`filters.${index}.operator`}
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      slotProps={{
                        input: {
                          sx: {
                            width: 150,
                          },
                        },
                      }}
                    >
                      {Object.values(TableColumnFilterOperator).map((operator) => (
                        <MenuItem key={operator} value={operator}>
                          {operator}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />

                {!OPERATORS_WITHOUT_VALUE.includes(filters[index]?.operator) && (
                  <Controller
                    name={`filters.${index}.text`}
                    control={control}
                    render={({ field }) => <TextField {...field} placeholder="Enter text..." />}
                  />
                )}

                <IconButton size="small" color="error" onClick={() => handleDeleteClicked(index)}>
                  <DeleteOutlineRounded />
                </IconButton>
              </Stack>
            ))}
          </Stack>
          <Stack direction="row" spacing={0.5}>
            <Button variant="tertiary" onClick={handleAddFilterClicked}>
              Add Filter
            </Button>
            <Button variant="tertiary" onClick={() => reset()}>
              Remove All
            </Button>
          </Stack>
        </Stack>
      </Menu>
    </>
  );
};

export { TableFiltersButton };
